import { Accordion, Box, Button, Checkbox, Field, Icon, Input, Portal, Select, Text } from "@chakra-ui/react"
import { closestCenter, DndContext } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { SortableItem } from "../Common/SortableItem"
import { FiMenu, FiPlusCircle, FiTrash2 } from "react-icons/fi"
import { Tooltip } from "../UI/tooltip"
import { useEffect, useRef, useState } from "react"
import { addBlock, LandingpageCategoriesProps, onDragEnd, removeBlock } from "../../api/landingpage"
import { Controller } from "react-hook-form"


function LandingpageCategories({
    form,
    fieldArray,
    categoryBlocks,
    categoryList,
    categoryCollection,
    ct_read
} : LandingpageCategoriesProps) {
    const { register, control, formState: {errors} } = form
    const [openItems, setOpenItems] = useState<string[]>([])

    const prevIds = useRef<string[]>([])
    useEffect(() => {
        const currentIds = fieldArray.fields.map(f => f.id)

        if (currentIds.length > prevIds.current.length) {
            const addedId = currentIds.find(id => !prevIds.current.includes(id))
            if (addedId) {
                setOpenItems(prev => [...prev, addedId])
            }
        }

        prevIds.current = currentIds
    }, [fieldArray.fields])

    return (
        <Box border="1px solid" borderColor="dim" borderRadius={8}>
            <DndContext collisionDetection={closestCenter} onDragEnd={(e) => onDragEnd(e, form, fieldArray)} onDragStart={() => setOpenItems([])}>
                <SortableContext items={fieldArray.fields?.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    <Accordion.Root multiple={true} value={openItems} onValueChange={(details) => setOpenItems(details.value)}>
                        {fieldArray.fields?.map((block, i) => (
                            <SortableItem id={block.id} key={block.id}>
                                {({ listeners }) => (
                                    <Accordion.Item
                                        value={block.id}
                                        px={2}
                                        py={2}
                                        divideY="1px"
                                        divideColor="main"
                                    >
                                        <Accordion.ItemTrigger h="2.5em">
                                            <Icon as={FiMenu} {...listeners} data-testid="drag_lp_category" cursor="pointer" _active={{cursor: "grabbing"}} />

                                            <Box
                                                flex="1"
                                                textAlign="left"
                                                color={errors?.categories?.landingpage_categories?.length != undefined ? "danger" : undefined}
                                                data-testid="title_lp_category"
                                            >
                                                {categoryBlocks[i]?.title && categoryBlocks[i]?.title != "" ? categoryBlocks[i].title :  "Neue Kategorie"}

                                                <Accordion.ItemIndicator display="none" />
                                            </Box>

                                            <Icon
                                                as={FiTrash2}
                                                color="danger"
                                                onClick={(e) => { e.stopPropagation(), removeBlock(block.id, form, fieldArray) }}
                                                data-testid="delete_lp_category"
                                                cursor="pointer"
                                            />
                                        </Accordion.ItemTrigger>

                                        <Accordion.ItemContent pt={4} borderRadius="none">
                                            <Accordion.ItemBody>
                                                {/* Is active */}
                                                <Controller
                                                    control={control}
                                                    name={`categories.landingpage_categories.${i}.is_active`}
                                                    defaultValue={true}
                                                    render={({ field }) => (
                                                    <Field.Root>
                                                        <Checkbox.Root
                                                            cursor="pointer"
                                                            checked={field.value}
                                                            onCheckedChange={({ checked }) => field.onChange(checked)}
                                                        >
                                                        <Checkbox.HiddenInput />
                                                        <Checkbox.Control />
                                                        <Checkbox.Label>Aktiv</Checkbox.Label>
                                                        </Checkbox.Root>
                                                    </Field.Root>
                                                )} />

                                                {/* Title */}
                                                <Field.Root mt={4} invalid={!!errors?.categories?.landingpage_categories?.[i]?.title} required>
                                                    <Field.Label htmlFor="title">
                                                        Titel
                                                        <Field.RequiredIndicator />
                                                    </Field.Label>

                                                    <Input
                                                        id="title"
                                                        {...register(`categories.landingpage_categories.${i}.title`, {
                                                                required: "Titel ist erforderlich",
                                                                maxLength: {
                                                                    value: 255,
                                                                    message: "Titel darf maximal 255 Zeichen lang sein",
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        placeholder="Titel"
                                                    />

                                                    {errors?.categories?.landingpage_categories?.[i]?.title && (
                                                        <Field.ErrorText>{errors.categories.landingpage_categories?.[i]?.title?.message}</Field.ErrorText>
                                                    )}
                                                </Field.Root>

                                                {/* Number of elements */}
                                                <Field.Root mt={4} invalid={!!errors?.categories?.landingpage_categories?.[i]?.publication_display_amount} required>
                                                    <Field.Label htmlFor="publication_display_amount">
                                                        Anzahl Elemente
                                                        <Field.RequiredIndicator />
                                                    </Field.Label>

                                                    <Input
                                                        min={1}
                                                        max={10}
                                                        id="publication_display_amount"
                                                        {...register(`categories.landingpage_categories.${i}.publication_display_amount`, {
                                                                required: "Anzahl Elemente ist erforderlich",
                                                                max: {
                                                                    value: 10,
                                                                    message: "Es kann maximum 10 angegeben werden"
                                                                },
                                                                min: {
                                                                    value: 1,
                                                                    message: "Es muss minimum 1 angegeben werden"
                                                                }
                                                            })
                                                        }
                                                        type="number"
                                                        placeholder="Anzahl Elemente"
                                                    />

                                                    {errors?.categories?.landingpage_categories?.[i]?.publication_display_amount && (
                                                        <Field.ErrorText>{errors.categories.landingpage_categories?.[i]?.publication_display_amount?.message}</Field.ErrorText>
                                                    )}
                                                </Field.Root>

                                                {/* Category */}
                                                <Field.Root mt={4} required invalid={!!errors?.categories?.landingpage_categories?.[i]?.fk_category_id}>
                                                    <Field.Label htmlFor="fk_category_id">
                                                        Kategorie
                                                        <Field.RequiredIndicator />
                                                    </Field.Label>

                                                    {ct_read ? (
                                                        <Controller
                                                            control={control}
                                                            name={`categories.landingpage_categories.${i}.fk_category_id`}
                                                            rules={{
                                                                required: "Kategorie ist erforderlich"
                                                            }}
                                                            render={({ field }) => (
                                                                <Select.Root
                                                                    multiple={false}
                                                                    collection={categoryCollection}
                                                                    name={field.name}
                                                                    value={field.value ? [field.value] : []}
                                                                    onValueChange={(e) => { field.onChange(e.value?.[0] ?? null) }}
                                                                    onInteractOutside={field.onBlur}
                                                                    onPointerDownOutside={field.onBlur}
                                                                    positioning={{ placement: "bottom", sameWidth: true }}
                                                                >
                                                                    <Select.HiddenSelect />

                                                                    <Select.Control>
                                                                        <Select.Trigger>
                                                                            <Select.ValueText placeholder="Kategorie" />
                                                                        </Select.Trigger>

                                                                        <Select.IndicatorGroup>
                                                                            <Select.Indicator />
                                                                        </Select.IndicatorGroup>
                                                                    </Select.Control>

                                                                    <Portal>
                                                                        <Select.Positioner>
                                                                            <Select.Content>
                                                                                {categoryList?.data ? (
                                                                                    categoryList?.data?.map(c => (
                                                                                        <Select.Item item={c.id} key={c.id}>
                                                                                            {c.name}
                                                                                            <Select.ItemIndicator />
                                                                                        </Select.Item>
                                                                                    ))
                                                                                ) : (
                                                                                    <Text>Keine Kategorien vorhanden</Text>
                                                                                )}
                                                                            </Select.Content>
                                                                        </Select.Positioner>
                                                                    </Portal>
                                                                </Select.Root>
                                                            )}
                                                        />
                                                    ) : (
                                                        <Text color="danger">Keine Berechtigung für Kategorien</Text>
                                                    )}

                                                    {errors?.categories?.landingpage_categories?.[i]?.fk_category_id &&
                                                        <Field.ErrorText>{errors.categories.landingpage_categories?.[i]?.fk_category_id?.message}</Field.ErrorText>
                                                    }
                                                </Field.Root>
                                            </Accordion.ItemBody>
                                        </Accordion.ItemContent>
                                    </Accordion.Item>
                                )}
                            </SortableItem>
                        ))}
                    </Accordion.Root>
                </SortableContext>
            </DndContext>

            <Tooltip showArrow content="Block hinzufügen" openDelay={500}>
                <Box>
                    <Button
                        onClick={() => addBlock(fieldArray)}
                        w="100%"
                        h="fit-content"
                        py={2}
                        backgroundColor="transparent"
                        color="main"
                        data-testid="add_lp_category"
                    >
                        <Icon as={FiPlusCircle} boxSize="25px" />
                    </Button>
                </Box>
            </Tooltip>
        </Box>
    )
}

export default LandingpageCategories